import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

// Le sezioni della pagina si caricano in modo asincrono e quelle sopra
// l'ancora continuano a cambiare altezza anche dopo il primo scroll: la vista
// viene quindi riallineata a ogni frame per questa finestra di tempo, cosi'
// l'ancora resta ferma mentre il contenuto sopra si popola. Il riallineamento
// si interrompe appena l'utente interagisce con la pagina.
const PIN_DURATION_MS = 8000;
const USER_EVENTS = ["wheel", "touchstart", "keydown", "mousedown"] as const;

/**
 * Porta la vista sull'elemento indicato dall'hash della URL e la mantiene
 * allineata finche' il contenuto sopra l'ancora ha finito di caricarsi.
 * `ready` permette di attendere i dati principali della pagina.
 */
export const useScrollToHash = (ready: boolean = true) => {
  const { hash } = useLocation();
  const handledHash = useRef<string>();

  useEffect(() => {
    if (!ready || !hash || handledHash.current === hash) return;
    handledHash.current = hash;

    const targetId = hash.slice(1);
    const startedAt = Date.now();
    let frameId = 0;

    const stop = () => {
      cancelAnimationFrame(frameId);
      USER_EVENTS.forEach((event) => window.removeEventListener(event, stop));
    };

    const align = () => {
      const element = document.getElementById(targetId);
      if (element && typeof element.scrollIntoView === "function") {
        element.scrollIntoView({ behavior: "auto", block: "start" });
      }
      if (Date.now() - startedAt > PIN_DURATION_MS) {
        stop();
        return;
      }
      frameId = requestAnimationFrame(align);
    };

    USER_EVENTS.forEach((event) =>
      window.addEventListener(event, stop, { passive: true }),
    );
    frameId = requestAnimationFrame(align);

    return stop;
  }, [hash, ready]);
};
